import React from 'react';

/**
 * HeroCard — carte pleine largeur, valeur principale de la page.
 *
 * Props :
 *   year:                       number
 *   mainLabel:                  string  ("Coût total employeur 2026")
 *   mainValue:                  string  ("4'013'020 CHF")
 *   comparisonValue:            string  ("3'880'500 CHF")
 *   deltaText:                  string  ("+132'520")
 *   deltaPctText:               string  ("+3,4%")
 *   deltaSign:                  'positive' | 'negative' | 'neutral'
 *                               positive = hausse de coût = défavorable = terracotta
 *                               (la convention couleur favorabilité est portée par le parent)
 *   sublineRight:               string  ("dont volume 78% · prix 22%")
 *   bridge:                     [{ op?, label, value, valueColor?, subline }]
 *                               décomposition volume/prix rendue en pied de carte,
 *                               inline (sans blocs imbriqués). `op` = séparateur
 *                               affiché AVANT le segment ('+', '=').
 */
export default function HeroCard({
  year,
  mainLabel,
  mainValue,
  comparisonValue,
  deltaText,
  deltaPctText,
  deltaSign,
  sublineRight,
  bridge,
}) {
  return (
    <div style={cardStyle}>
      <div style={headStyle}>{mainLabel}</div>
      <div style={topRowStyle}>
        <div style={leftStyle}>
          <div style={mainValueStyle}>{mainValue}</div>
          <div style={sublineStyle}>
            vs {year - 1} : <span style={numericInline}>{comparisonValue}</span>
          </div>
        </div>
        <div style={rightStyle}>
          <div style={{ ...deltaLineStyle, color: deltaColor(deltaSign) }}>
            <span style={numericInline}>{deltaText}</span>
          </div>
          <div style={{ ...deltaLineStyle, color: deltaColor(deltaSign) }}>
            <span style={numericInline}>{deltaPctText}</span>
          </div>
          {sublineRight && <div style={sublineStyle}>{sublineRight}</div>}
        </div>
      </div>

      {bridge && bridge.length > 0 && (
        <div style={bridgeRowStyle}>
          {bridge.map((seg, i) => (
            <React.Fragment key={i}>
              {seg.op && <span style={sepStyle}>{seg.op}</span>}
              <div style={segStyle}>
                <div style={segLabelStyle}>{seg.label}</div>
                <div style={{ ...segValueStyle, color: seg.valueColor || '#2B203E' }}>{seg.value}</div>
                {seg.subline && <div style={segSublineStyle}>{seg.subline}</div>}
              </div>
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
}

function deltaColor(sign) {
  if (sign === 'positive') return '#5C7A3E';
  if (sign === 'negative') return '#BA5D4A';
  return '#2B203E';   // neutre = navy (pas d'alerte sur cette page)
}

const cardStyle = {
  background: 'var(--color-surface-card)',
  border: '0.5px solid var(--color-border)',
  borderRadius: 'var(--radius-card)',
  padding: '18px 22px',
  marginBottom: 12,
  display: 'flex',
  flexDirection: 'column',
};

/* En-tête de section = label petites capitales + filet bas, À L'INTÉRIEUR de la
   carte blanche (pas de bande pleine ni de bleed : éviter le card-dans-card). */
const headStyle = {
  marginBottom: 16,
  paddingBottom: 12,
  borderBottom: '1px solid var(--color-border)',
  fontSize: 11,
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  fontWeight: 600,
  color: 'var(--color-text-muted)',
};

const topRowStyle = {
  display: 'grid',
  gridTemplateColumns: '1fr auto',
  alignItems: 'baseline',
  gap: 24,
};

const leftStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
};

const rightStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
  gap: 2,
};


const mainValueStyle = {
  fontFamily: 'JetBrains Mono, monospace',
  fontVariantNumeric: 'tabular-nums',
  fontSize: 28,
  fontWeight: 500,
  color: '#2B203E',
  lineHeight: 1.1,
};

const sublineStyle = {
  fontSize: 12,
  color: '#6b6253',
};

const deltaLineStyle = {
  fontFamily: 'JetBrains Mono, monospace',
  fontVariantNumeric: 'tabular-nums',
  fontSize: 13,
  fontWeight: 500,
  lineHeight: 1.3,
};

const numericInline = {
  fontFamily: 'JetBrains Mono, monospace',
  fontVariantNumeric: 'tabular-nums',
};

/* ── Bridge footer (inline, sans blocs) ──────────────────────────────────── */

const bridgeRowStyle = {
  display: 'flex',
  alignItems: 'flex-end',
  flexWrap: 'wrap',
  gap: 14,
  marginTop: 16,
  paddingTop: 14,
  borderTop: '0.5px solid var(--color-border)',
};

const segStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: 1,
};

const segLabelStyle = {
  fontSize: 11,
  color: '#6b6253',
  fontWeight: 500,
};

const segValueStyle = {
  fontFamily: 'JetBrains Mono, monospace',
  fontVariantNumeric: 'tabular-nums',
  fontSize: 14,
  fontWeight: 500,
  lineHeight: 1.25,
};

const segSublineStyle = {
  fontSize: 10.5,
  color: '#6b6253',
  fontFamily: 'JetBrains Mono, monospace',
  fontVariantNumeric: 'tabular-nums',
};

const sepStyle = {
  fontSize: 15,
  fontWeight: 400,
  color: '#9b9282',
  alignSelf: 'center',
  paddingBottom: 2,
};
